import { Badge, Chip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import useUnreadCount from 'hooks/useUnreadCount';
import React from 'react';
import { menuItemIdToUnreadCountKey } from 'utils/chat-sidebar-unread-view';

interface UnreadIndicatorProps {
  menuItemId?: string | undefined;
  counterTone?: 'urgent' | 'newWork' | 'muted';
  asBadge?: boolean;
  children?: React.ReactNode;
  visible?: boolean;
  /** כשמוגדר — מונה שיחות מחושב בצד הלקוח (טאב סרגל), בלי מפתח Redux גלובלי */
  countOverride?: number;
  /** יישור אנכי של העיגול מול אייקון הסטטוס (סרגל שיחות) */
  badgeVerticallyCentered?: boolean;
}

const UnreadIndicator: React.FC<UnreadIndicatorProps> = ({
  menuItemId,
  counterTone,
  asBadge,
  visible = true,
  children,
  countOverride,
  badgeVerticallyCentered = false,
}) => {
  const theme = useTheme();
  const countKey = menuItemIdToUnreadCountKey(menuItemId);
  const unreadFromStore = useUnreadCount(countKey);
  const unreadCount = countOverride !== undefined ? countOverride : unreadFromStore;
  const isZero = unreadCount === 0;

  const zeroBadgeSx = {
    '& .MuiBadge-badge': {
      minWidth: 16,
      height: 16,
      fontSize: 10,
      fontWeight: 600,
      bgcolor: alpha(theme.palette.grey[500], theme.palette.mode === 'dark' ? 0.22 : 0.14),
      color: theme.palette.text.disabled,
      border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
    },
  };

  const positiveBadgeSx =
    counterTone === 'urgent'
      ? {
          '& .MuiBadge-badge': {
            minWidth: 16,
            height: 16,
            bgcolor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
          },
        }
      : counterTone === 'newWork'
        ? {
            '& .MuiBadge-badge': {
              minWidth: 16,
              height: 16,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontWeight: 700,
            },
          }
        : counterTone === 'muted'
          ? {
              '& .MuiBadge-badge': {
                minWidth: 16,
                height: 16,
                bgcolor: theme.palette.info.main,
                color: theme.palette.info.contrastText,
              },
            }
          : {
              '& .MuiBadge-badge': {
                minWidth: 16,
                height: 16,
                bgcolor: theme.palette.secondary.main,
                color: theme.palette.secondary.contrastText,
              },
            };

  const chipPositive =
    counterTone === 'urgent'
      ? {
          bgcolor: theme.palette.warning.main,
          color: theme.palette.warning.contrastText,
          fontWeight: 700,
        }
      : counterTone === 'newWork'
        ? {
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            fontWeight: 700,
          }
        : counterTone === 'muted'
          ? {
              bgcolor: theme.palette.info.main,
              color: theme.palette.info.contrastText,
              fontWeight: 600,
            }
          : {
              bgcolor: theme.palette.info.main,
              color: theme.palette.info.contrastText,
              fontWeight: 600,
            };

  /** כשהמגירה פתוחה לא מציגים Badge על האייקון — רק Chip בצד; בלי unmount ל-Badge נשארת לעיתים שכבה כפולה (אפור+סגול). */
  if (asBadge && !visible) {
    return <>{children}</>;
  }

  const badgeSlotSx = {
    minWidth: 16,
    height: 16,
    ...(isZero
      ? (zeroBadgeSx as { '& .MuiBadge-badge': Record<string, unknown> })['& .MuiBadge-badge']
      : (positiveBadgeSx as { '& .MuiBadge-badge': Record<string, unknown> })['& .MuiBadge-badge']),
    ...(badgeVerticallyCentered
      ? {
          top: '50%',
          transform: 'translateY(-50%)',
          insetInlineEnd: -2,
          insetInlineStart: 'auto' as const,
        }
      : {}),
  };

  const centeredBadgeSx = badgeVerticallyCentered
    ? {
        '& .MuiBadge-badge': {
          top: '50%',
          transform: 'translateY(-50%)',
          insetInlineEnd: -2,
          insetInlineStart: 'auto' as const,
        },
      }
    : {};

  return asBadge ? (
    <Badge
      badgeContent={unreadCount}
      color="secondary"
      sx={{
        ...(isZero ? zeroBadgeSx : positiveBadgeSx),
        ...centeredBadgeSx,
      }}
      invisible={!visible}
      overlap="circular"
    >
      {children}
    </Badge>
  ) : (
    <Chip
      label={unreadCount}
      size="small"
      variant={isZero ? 'outlined' : 'filled'}
      sx={{
        width: 24,
        height: 24,
        alignSelf: badgeVerticallyCentered ? 'center' : undefined,
        '& .MuiChip-label': {
          px: 0.5,
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        ...(isZero
          ? {
              opacity: 0.85,
              fontWeight: 500,
              bgcolor: alpha(theme.palette.grey[500], theme.palette.mode === 'dark' ? 0.12 : 0.08),
              color: theme.palette.text.disabled,
              borderColor: alpha(theme.palette.divider, 0.85),
            }
          : chipPositive),
      }}
    />
  );
};

export default UnreadIndicator;
